import React, { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import BreadCrumbs from 'components/common/bread_crumbs';
import * as routes from 'constants/routes';
import { combineClassNames, getMediaPath } from 'app/functions';
import {
    extractHrefFromHtmlLink,
    extractLinkLabelFromHtml,
} from '../utils/hotYoterHtml';
import { HotYoterPageInfo } from './HotYoterPageInfo';
import { HotYoterHowToGetCarousel } from './HotYoterHowToGetCarousel';

const DEFAULT_REGULATIONS_LABEL = 'לתקנון הטבות הוטיותר';

const TABLET_UP = '@media (min-width: 768px)';
const MOBILE_DOWN = '@media (max-width: 767px)';

const IntroSectionRoot = styled('section', {
    shouldForwardProp: (prop) => prop !== 'hasBackgroundImage',
})(({ theme, hasBackgroundImage }) => ({
    position: 'relative',
    boxSizing: 'border-box',
    overflow: 'hidden',
    width: '100%',
    backgroundColor: theme.palette.background.lightBlue,
    borderRadius: 0,
    padding: `${theme.spacing(2)} 0 ${theme.spacing(1.25)}`,
    marginTop: theme.spacing(1),
    [MOBILE_DOWN]: {
        width: '100vw',
        maxWidth: '100vw',
        marginInline: 'calc(50% - 50vw)',
    },
    [TABLET_UP]: {
        borderRadius: theme.spacing(2),
        marginLeft: 0,
        marginRight: 0,
        padding: theme.spacing(2.5, 3),
        ...(hasBackgroundImage && {
            backgroundColor: 'transparent',
            padding: theme.spacing(2.75, 2, 2.25, 2.75),
        }),
    },
    [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(-1),
        ...(hasBackgroundImage && {
            padding: theme.spacing(2.5, 2, 2, 2.75),
        }),
    },
    [theme.breakpoints.between('md', 'lg')]: {
        ...(hasBackgroundImage && {
            padding: theme.spacing(2.75, 2.25, 2, 2.75),
        }),
    },
    [theme.breakpoints.up('lg')]: {
        marginTop: theme.spacing(-2),
        ...(hasBackgroundImage ? {
            padding: theme.spacing(3.25, 2.75, 2.75, 3.25),
        } : {
            padding: theme.spacing(3, 3.5),
        }),
    },
    [theme.breakpoints.up('xl')]: {
        ...(hasBackgroundImage ? {
            padding: theme.spacing(4, 3.5, 3.5, 4),
        } : {
            padding: theme.spacing(3.5, 4),
        }),
    },
}));

const IntroBackgroundImage = styled('img')({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'fill',
    objectPosition: 'center',
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: 0,
    borderRadius: 'inherit',
});

const IntroSectionBody = styled('div', {
    shouldForwardProp: (prop) => prop !== 'hasBackgroundImage',
})(({ theme, hasBackgroundImage }) => ({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    [TABLET_UP]: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: theme.spacing(2),
        ...(hasBackgroundImage && {
            gap: theme.spacing(1.5),
            alignItems: 'center',
        }),
    },
    [theme.breakpoints.up('md')]: {
        gap: theme.spacing(2),
        ...(hasBackgroundImage && {
            gap: theme.spacing(1.5),
        }),
    },
    [theme.breakpoints.between('md', 'lg')]: {
        ...(hasBackgroundImage && {
            gap: theme.spacing(1.25),
            alignItems: 'center',
        }),
    },
    [theme.breakpoints.up('lg')]: {
        gap: theme.spacing(2.5),
        ...(hasBackgroundImage && {
            gap: theme.spacing(2),
        }),
    },
    [theme.breakpoints.up('xl')]: {
        gap: theme.spacing(4),
        ...(hasBackgroundImage && {
            gap: theme.spacing(3),
        }),
    },
}));

const IntroCardsColumn = styled('div', {
    shouldForwardProp: (prop) => prop !== 'cardCount',
})(({ theme, cardCount }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    width: '100%',
    minWidth: 0,
    '--hot-yoter-card-count': cardCount ?? 4,
    '--hot-yoter-card-gap': theme.spacing(1),
    [TABLET_UP]: {
        flex: 1,
        gap: theme.spacing(1),
        '--hot-yoter-card-gap': theme.spacing(1.25),
    },
    [theme.breakpoints.up('lg')]: {
        gap: theme.spacing(1.25),
        '--hot-yoter-card-gap': theme.spacing(1.5),
    },
}));

const RegulationsLink = styled('button', {
    shouldForwardProp: (prop) => prop !== 'showOnMobile',
})(({ theme, showOnMobile }) => ({
    display: showOnMobile ? 'block' : 'none',
    width: '100%',
    marginTop: theme.spacing(0.75),
    border: 'none',
    background: 'transparent',
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.35,
    textAlign: 'center',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    padding: 0,
    [MOBILE_DOWN]: {
        paddingInline: 'calc(14 / 320 * 100vw)',
        boxSizing: 'border-box',
        '@media (min-width: 600px)': {
            fontSize: theme.typography.pxToRem(13),
        },
    },
}));

export const HotYoterIntroSection = ({
    pageTitle,
    headerText,
    howToGet = {},
    regulationsUrl,
    onRegulationsPress,
    testID = 'hot-yoter-intro-section',
}) => {
    const deviceState = useSelector((store) => store.state.deviceState);
    const isMobile = deviceState === 'mobile';
    const isTabletUp = useMediaQuery('(min-width:768px)');
    const desktopBackgroundImage = howToGet.desktopBackgroundImage ?? null;
    const hasBackgroundImage = Boolean(desktopBackgroundImage) && isTabletUp;
    const backgroundImageUrl = hasBackgroundImage
        ? getMediaPath(desktopBackgroundImage)
        : null;
    const sections = howToGet.howToGetSections ?? [];
    const regulationsLink = extractHrefFromHtmlLink(headerText?.link)
        ?? headerText?.linkUrl
        ?? headerText?.regulationsUrl
        ?? regulationsUrl
        ?? null;
    const regulationsLabel = extractLinkLabelFromHtml(headerText?.link)
        ?? headerText?.linkLabel
        ?? DEFAULT_REGULATIONS_LABEL;
    const pageInfoShowsLink = Boolean(
        extractHrefFromHtmlLink(headerText?.link)
        ?? headerText?.linkUrl
        ?? headerText?.regulationsUrl,
    );
    const [cardRowHeight, setCardRowHeight] = useState(null);

    const handleRegulationsPress = () => {
        if (regulationsLink) {
            onRegulationsPress?.(regulationsLink);
        }
    };

    return (
        <IntroSectionRoot
            className={combineClassNames(
                'hot-yoter-intro-section',
                hasBackgroundImage && 'hot-yoter-intro-section_has-bg',
            )}
            hasBackgroundImage={hasBackgroundImage}
            testId={testID}
            dir="rtl"
        >
            {backgroundImageUrl ? (
                <IntroBackgroundImage
                    src={backgroundImageUrl}
                    alt=""
                    aria-hidden="true"
                    testId={`${testID}-background`}
                />
            ) : null}
            <IntroSectionBody
                className="hot-yoter-intro-section__body"
                hasBackgroundImage={hasBackgroundImage}
            >
                <HotYoterPageInfo
                    headerText={headerText}
                    pageTitle={pageTitle}
                    hasBackgroundImage={hasBackgroundImage}
                    cardRowHeight={isTabletUp && !isMobile ? cardRowHeight : null}
                    regulationsLink={regulationsLink}
                    regulationsLabel={regulationsLabel}
                    onRegulationsPress={onRegulationsPress}
                />
                <IntroCardsColumn cardCount={sections.length || undefined}>
                    <HotYoterHowToGetCarousel
                        sections={sections}
                        onCardRowHeightChange={setCardRowHeight}
                    />
                    {isMobile && regulationsLink ? (
                        <RegulationsLink
                            type="button"
                            showOnMobile={!pageInfoShowsLink}
                            onClick={handleRegulationsPress}
                            testId={`${testID}-regulations-link`}
                        >
                            {regulationsLabel}
                        </RegulationsLink>
                    ) : null}
                </IntroCardsColumn>
            </IntroSectionBody>
        </IntroSectionRoot>
    );
};

export const HotYoterPageHeader = ({ pageTitle, slug, showTitle = true }) => (
    <header className="hot-yoter-page-header">
        <BreadCrumbs
            root={{
                text: 'דף הבית',
                route: routes.home,
            }}
            crumbs={[
                {
                    text: pageTitle,
                    route: `${routes.hot_yoter}/${slug}`,
                },
            ]}
            className="hot-yoter-breadcrumbs"
        />
    </header>
);
